import { useFormContext } from 'react-hook-form';
import { FormValues } from '../schema';
import { useQuery } from '@tanstack/react-query';
import {
  MultiChainDWalletService,
  multiChainDWalletServiceKeys,
} from 'api/services/httpServices/DWalletService';
import Big from 'big.js';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Tooltip from 'modules/shared-components/tooltip';
import { chainAsset, minimumSafeAmount } from 'api/types/httpsTypes/d-wallets';
import { useUserState } from 'modules/user/UserContext';
import { NotificationDex } from '../../components/alerts/notification';

export function TradeWarnings() {
  const { watch } = useFormContext<FormValues>();
  const buyAmount = watch('buyOrder.entries.amount');
  const orders = watch('buyOrder.entries.orders') || [];
  const selectedWallets = watch('wallets') || [];
  const chain = watch('chain');

  const { user } = useUserState();
  const { data } = useQuery({
    queryFn: () => MultiChainDWalletService.getWallets(chain),
    queryKey: multiChainDWalletServiceKeys.getWallets(chain),
    enabled: !!chain && !!user,
  });

  const selectedWalletsData =
    selectedWallets && selectedWallets.length > 0
      ? selectedWallets.map((wallet) => data?.find((w) => w.id === wallet))
      : null;

  const totalLimitOrdersAmount = orders.reduce(
    (state, next) => state.plus(next.amount || 0),
    Big(0)
  );

  if (
    !selectedWalletsData ||
    ((!buyAmount || isNaN(buyAmount)) && !orders.length)
  ) {
    return null;
  }

  const walletsBelowBalance = selectedWalletsData.filter(
    (wallet) =>
      wallet &&
      (Big(wallet.chainBalance).lt(buyAmount ?? 0) ||
        Big(wallet.chainBalance).lt(totalLimitOrdersAmount))
  )?.length;

  return (
    <>
      {walletsBelowBalance > 0 && (
        <div className="">
          <NotificationDex
            className="xxs:mx-2 lg:mx-0"
            size="xSmall"
            type="warning"
          >
            Trading with insufficient funds can lead to missed trades. Top-up.
          </NotificationDex>
        </div>
      )}
    </>
  );
}

export function WalletBelowLimitWarning() {
  const { watch } = useFormContext<FormValues>();
  const selectedWallets = watch('wallets') || [];
  const chain = watch('chain');
  const { user } = useUserState();
  const { data } = useQuery({
    queryFn: () => MultiChainDWalletService.getWallets(chain),
    queryKey: multiChainDWalletServiceKeys.getWallets(chain),
    enabled: !!chain && !!user,
  });

  const selectedWalletsData =
    selectedWallets && selectedWallets.length > 0
      ? selectedWallets.map((wallet) => data?.find((w) => w.id === wallet))
      : null;

  const walletsBalanceBelowLimit = selectedWalletsData
    ? selectedWalletsData.filter(
        (wallet) =>
          wallet && Big(wallet.chainBalance).lt(minimumSafeAmount(chain))
      )?.length
    : null;

  return (
    <>
      {walletsBalanceBelowLimit && walletsBalanceBelowLimit > 0 ? (
        <>
          <div className="hidden lg:block">
            <Tooltip
              text={`Warning! ${walletsBalanceBelowLimit} wallet(s) balance(s) are below ${minimumSafeAmount(
                chain
              )} ${chainAsset(chain)}. This could cause unexpected
                    errors while managing your positions. Deposit ${chainAsset(
                      chain
                    )} to prevent future issues.`}
            >
              <div className="flex space-x-1 items-center">
                <ExclamationTriangleIcon className="text-red-500 w-4 h-4" />
                <div className="xxs:text-dex-white-secondary">
                  {walletsBalanceBelowLimit} wallet(s) with low balance. Deposit{' '}
                  {chainAsset(chain)}.
                </div>
              </div>
            </Tooltip>
          </div>
          <div className="lg:hidden">
            <NotificationDex type="warning">
              {walletsBalanceBelowLimit} wallet(s) balance(s) are below
              {minimumSafeAmount(chain)} {chainAsset(chain)}. This could cause
              unexpected errors while managing your positions. Top-up.
            </NotificationDex>
          </div>
        </>
      ) : (
        ''
      )}
    </>
  );
}
